import { decryptEvernote, decryptText, encryptText, WrongPassphrase } from "@engram/core/crypt";
import { getHTMLFromFragment, Node, type Editor as TEditor } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer, type ReactNodeViewProps } from "@tiptap/react";
import DOMPurify from "dompurify";
import { Lock, LockOpen } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";

/*
 * Evernote's "Encrypt selected text": the selection becomes a locked block holding its HTML encrypted
 * with a passphrase (AES-256-GCM, see core/crypt.ts). Showing it asks for the passphrase and displays
 * the text read-only until the note closes; "Decrypt permanently" puts the text back. Imported Evernote
 * <en-crypt> blocks (format "evernote") open the same way when they use Evernote's AES format.
 *   <div data-encrypted="v1" data-cipher="…" data-hint="…">Encrypted text</div>
 */

type Format = "v1" | "evernote";

export const Encrypted = Node.create({
  name: "encrypted",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      format: {
        default: "v1",
        parseHTML: (el) => (el.hasAttribute("data-en-crypt") || el.getAttribute("data-encrypted") === "evernote" ? "evernote" : "v1"),
        renderHTML: (a) => ({ "data-encrypted": a.format }),
      },
      cipher: {
        default: "",
        parseHTML: (el) => (el.getAttribute("data-cipher") ?? el.getAttribute("data-en-crypt") ?? "").replace(/\s/g, ""),
        renderHTML: (a) => ({ "data-cipher": a.cipher }),
      },
      hint: { default: "", parseHTML: (el) => el.getAttribute("data-hint") ?? "", renderHTML: (a) => (a.hint ? { "data-hint": a.hint } : {}) },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-encrypted]" }, { tag: "div[data-en-crypt]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", HTMLAttributes, "Encrypted text"];
  },
  renderText() {
    return "Encrypted text";
  },
  addNodeView() {
    return ReactNodeViewRenderer(EncryptedView);
  },
});

/** Decrypted text, per block, until the page reloads (Evernote keeps it open while the note is open). */
const opened = new Map<string, string>();

async function decrypt(format: Format, cipher: string, pass: string): Promise<string> {
  return format === "evernote" ? decryptEvernote(cipher, pass) : decryptText(cipher, pass);
}

function EncryptedView({ node, editor, getPos, selected }: ReactNodeViewProps) {
  const { cipher, hint, format } = node.attrs as { cipher: string; hint: string; format: Format };
  const [html, setHtml] = useState<string | null>(opened.get(cipher) ?? null);
  const [asking, setAsking] = useState(false);
  const hide = () => {
    opened.delete(cipher);
    setHtml(null);
  };
  const permanently = () => {
    const pos = getPos();
    if (pos == null || html == null) return;
    opened.delete(cipher);
    editor.chain().focus().insertContentAt({ from: pos, to: pos + node.nodeSize }, html).run();
  };
  return (
    <NodeViewWrapper className={`encrypted${selected ? " selected" : ""}${html != null ? " open" : ""}`} data-drag-handle="">
      {html == null ? (
        <button className="encrypted-lock" contentEditable={false} onMouseDown={(e) => e.preventDefault()} onClick={() => setAsking(true)}>
          <Lock size={15} />
          <span>Encrypted text</span>
          {hint && <span className="encrypted-hint">Hint: {hint}</span>}
          <span className="encrypted-show">Show</span>
        </button>
      ) : (
        <div contentEditable={false}>
          <div className="encrypted-bar">
            <LockOpen size={14} /> <span>Decrypted text (read-only)</span>
            <span className="sketch-gap" />
            {editor.isEditable && (
              <button className="btn small" onClick={permanently}>
                Decrypt permanently
              </button>
            )}
            <button className="btn small" onClick={hide}>
              Hide
            </button>
          </div>
          <div className="encrypted-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
        </div>
      )}
      {asking && (
        <PassDialog
          title="Show encrypted text"
          hint={hint}
          onClose={() => setAsking(false)}
          onSubmit={async (pass) => {
            const plain = await decrypt(format, cipher, pass);
            opened.set(cipher, plain);
            setHtml(plain);
          }}
        />
      )}
    </NodeViewWrapper>
  );
}

function PassDialog({
  title,
  hint,
  confirm,
  onSubmit,
  onClose,
}: {
  title: string;
  hint?: string;
  /** Encrypting: ask twice and take a hint. */
  confirm?: boolean;
  onSubmit: (pass: string, hint: string) => Promise<void>;
  onClose: () => void;
}) {
  const [pass, setPass] = useState("");
  const [again, setAgain] = useState("");
  const [newHint, setNewHint] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal"
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!pass || busy) return;
          if (confirm && pass !== again) return setError("The passphrases don't match.");
          setBusy(true);
          setError("");
          try {
            await onSubmit(pass, newHint.trim());
            onClose();
          } catch (err) {
            setError(err instanceof WrongPassphrase ? "Wrong passphrase." : err instanceof Error ? err.message : "Something went wrong.");
          } finally {
            setBusy(false);
          }
        }}
      >
        <h2>{title}</h2>
        {hint && <p className="muted">Hint: {hint}</p>}
        <label className="modal-label">Passphrase</label>
        <input className={`input${error ? " invalid" : ""}`} type="password" autoFocus autoComplete="off" value={pass} onChange={(e) => setPass(e.target.value)} />
        {confirm && (
          <>
            <label className="modal-label">Confirm passphrase</label>
            <input className="input" type="password" autoComplete="off" value={again} onChange={(e) => setAgain(e.target.value)} />
            <label className="modal-label">Hint (optional, not encrypted)</label>
            <input className="input" value={newHint} maxLength={200} onChange={(e) => setNewHint(e.target.value)} />
            <p className="muted small">Engram doesn't keep your passphrase. If you forget it, the text can't be recovered.</p>
          </>
        )}
        {error && <p className="modal-error">{error}</p>}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy || !pass}>
            {busy ? "Working…" : confirm ? "Encrypt" : "Show"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}

/** More → "Encrypt selected text…". */
export function encryptSelection(editor: TEditor) {
  const { from, to, empty } = editor.state.selection;
  if (empty) return;
  const html = getHTMLFromFragment(editor.state.doc.slice(from, to).content, editor.schema);
  const host = document.createElement("div");
  document.body.append(host);
  const root = createRoot(host);
  const close = () => {
    root.unmount();
    host.remove();
  };
  root.render(
    <PassDialog
      title="Encrypt selected text"
      confirm
      onClose={close}
      onSubmit={async (pass, hint) => {
        const cipher = await encryptText(html, pass);
        if (editor.isDestroyed) return;
        editor.chain().focus().insertContentAt({ from, to }, { type: "encrypted", attrs: { format: "v1", cipher, hint } }).run();
      }}
    />,
  );
}
